import { useMemo } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { TrendingUp, TrendingDown, Minus, BarChart3, Euro } from "lucide-react";

const DARK = "#002d26";

function linearTrend(values) {
  const n = values.length;
  if (n === 0) return { slope: 0, points: [] };
  let sx = 0;
  let sy = 0;
  let sxy = 0;
  let sxx = 0;
  values.forEach((y, x) => {
    sx += x;
    sy += y;
    sxy += x * y;
    sxx += x * x;
  });
  const denom = n * sxx - sx * sx;
  const slope = denom === 0 ? 0 : (n * sxy - sx * sy) / denom;
  const intercept = (sy - slope * sx) / n;
  const pts = values.map((_, x) => Math.max(0, slope * x + intercept));
  return { slope, points: pts };
}

const fmtMoney = (v) =>
  Number(v || 0).toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });

/**
 * Generischer Verlauf-Chart (Balken + Trendlinie).
 * props:
 *  - points: [{label, scans, new_users, revenue}]
 *  - dataKey: "scans" | "revenue"
 *  - title, valueLabel
 *  - isMoney: bool
 *  - unit: "Tag" | "Woche" | "Monat"
 *  - color, testId
 */
export function WeeklyTrendChart({
  points,
  dataKey = "scans",
  title = "Belege-Verlauf",
  valueLabel = "Belege",
  isMoney = false,
  unit = "Woche",
  color = "#00b29c",
  testId = "weekly-trend-chart",
}) {
  const { data, slope, total } = useMemo(() => {
    const list = points || [];
    const values = list.map((w) => Number(w[dataKey] || 0));
    const { slope: s, points: tp } = linearTrend(values);
    const rows = list.map((w, i) => ({
      label: w.label,
      value: Number(w[dataKey] || 0),
      trend: Math.round((tp[i] || 0) * 100) / 100,
    }));
    return { data: rows, slope: s, total: values.reduce((a, b) => a + b, 0) };
  }, [points, dataKey]);

  const fmt = (v) => (isMoney ? fmtMoney(v) : v);
  const eps = isMoney ? 0.5 : 0.05;
  const dir = slope > eps ? "up" : slope < -eps ? "down" : "flat";
  const dirMeta = {
    up: { Icon: TrendingUp, label: "steigend", cls: "text-primary" },
    down: { Icon: TrendingDown, label: "fallend", cls: "text-red-600" },
    flat: { Icon: Minus, label: "stabil", cls: "text-muted-foreground" },
  }[dir];
  const DirIcon = dirMeta.Icon;
  const perUnit = isMoney
    ? `${slope >= 0 ? "+" : ""}${fmtMoney(slope)}`
    : `${slope >= 0 ? "+" : ""}${Math.round(slope * 10) / 10}`;
  const HeadIcon = isMoney ? Euro : BarChart3;

  return (
    <div className="bg-white border border-border p-5 space-y-4" data-testid={testId}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-base font-medium flex items-center gap-2">
          <HeadIcon className="h-5 w-5 text-primary" />
          {title}
        </h2>
        <div
          className={`inline-flex items-center gap-1.5 text-sm font-medium ${dirMeta.cls}`}
          data-testid={`${testId}-direction`}
        >
          <DirIcon className="h-4 w-4" />
          <span>{dirMeta.label}</span>
          <span className="text-muted-foreground font-normal">
            ({perUnit}/{unit} · {fmt(total)} gesamt)
          </span>
        </div>
      </div>

      <div style={{ width: "100%", height: 260 }} data-testid={`${testId}-canvas`}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "#737373" }}
              axisLine={{ stroke: "#e5e5e5" }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={24}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11, fill: "#737373" }}
              axisLine={false}
              tickLine={false}
              width={isMoney ? 56 : 40}
              tickFormatter={(v) => (isMoney ? fmtMoney(v) : v)}
            />
            <Tooltip
              cursor={{ fill: "rgba(0,178,156,0.08)" }}
              formatter={(v, name) => [fmt(v), name === "value" ? valueLabel : "Trend"]}
              labelFormatter={(l) => `Zeitraum ${l}`}
              contentStyle={{ fontSize: 12 }}
            />
            <Bar dataKey="value" name={valueLabel} fill={color} maxBarSize={40} isAnimationActive={false} />
            <Line
              dataKey="trend"
              name="Trend"
              type="linear"
              stroke={DARK}
              strokeWidth={2}
              strokeDasharray="6 5"
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
